// Конструктор компоновок — список + редактор детали. Список и редактор
// живут в одном компоненте: переключаемся через локальный сигнал mode
// (без нагрузки роутера ради двух экранов).
//
// Секции отображаются как упорядоченный вертикальный список — top-to-bottom
// = забой → устье. Первая строка = долото (низ скважины), последняя =
// верхний элемент у устья. Position в DTO = индекс в массиве после
// сохранения; при PUT /sections мы шлём массив, бэк перенумеровывает 0..N-1.
// Конвенция «position 0 = долото» едина для всего стека (main_app schema v14,
// public-сайт, админка) — см. migration `m20260508_*_flip_drillstring_position`.
//
// Drag-n-drop: HTML5 native (без сторонних либ). Перетаскиваем за всю строку
// — input/select внутри ловят свои события первыми, поэтому редактирование
// полей DnD не ломает.

import { createSignal, createResource, createMemo, Show, For, onMount, onCleanup } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import {
  listDrillstrings,
  getDrillstring,
  createDrillstring,
  updateDrillstringHeader,
  deleteDrillstring,
  replaceSections,
  listShares,
  createShare,
  deleteShare,
} from './api/drillstrings'
import type {
  DrillstringSummaryDto,
  DrillstringSectionInput,
  ShareDto,
} from './api/drillstrings'
import { listTools } from './api/tool'
import type { ToolDto } from './api/tool'
import { kindLabel, KIND_BY_ID, TOOL_KINDS, fromSi, unwrapPayload } from './tool_kinds'
import type { ToolKind } from './tool_kind'
import { router } from './router/router'
import { DrillstringSchematic } from './DrillstringSchematic'

// ────────────────────── список ──────────────────────

type ListProps = {
  onOpen:   (id: number) => void
  onCreate: () => void
}

const DrillstringList: Component<ListProps> = (props) => {
  const [items, { refetch }] = createResource(listDrillstrings)
  const [delErr, setDelErr] = createSignal<string | null>(null)

  const onDelete = async (e: DrillstringSummaryDto) => {
    if (!confirm(`Удалить компоновку «${e.name}»? Секции тоже будут удалены.`)) return
    setDelErr(null)
    try {
      await deleteDrillstring(e.id)
      refetch()
    } catch (err) {
      setDelErr(String(err))
    }
  }

  return (
    <div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Компоновки</h1>
          <p class="page-subtitle">
            Сборки инструмента в порядке от устья к забою
          </p>
        </div>
        <div class="row">
          <button class="btn btn-primary" onClick={props.onCreate}>
            <Icon name="plus" />Компоновка
          </button>
        </div>
      </div>

      <Show when={delErr()}>
        <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
          {delErr()}
        </div>
      </Show>

      <Show when={items.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={items.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка: {String(items.error)}
        </div>
      </Show>

      <Show when={items() && items()!.length === 0}>
        <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
            Компоновок пока нет
          </div>
          <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-4)' }}>
            Создайте первую и добавьте в неё детали из каталога
          </div>
          <button class="btn btn-primary" onClick={props.onCreate}>
            <Icon name="plus" />Создать компоновку
          </button>
        </div>
      </Show>

      <Show when={items() && items()!.length > 0}>
        <div class="card">
          <table class="tbl">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>ID</th>
                <th>Название</th>
                <th>Описание</th>
                <th style={{ width: '160px' }}>Владелец</th>
                <th style={{ width: '180px' }}>Обновлено</th>
                <th style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              <For each={items()}>
                {(d) => (
                  <tr style={{ cursor: 'pointer' }} onClick={() => props.onOpen(d.id)}>
                    <td class="mono text-xs muted">#{d.id}</td>
                    <td style={{ 'font-weight': 500 }}>{d.name}</td>
                    <td class="text-xs muted">{d.description || '—'}</td>
                    <td class="text-xs">
                      <Show
                        when={d.is_owner}
                        fallback={
                          <span title={d.owner_email ?? ''} class="muted">
                            от {d.owner_name || d.owner_email || '?'}
                          </span>
                        }
                      >
                        <span class="muted">я</span>
                      </Show>
                    </td>
                    <td class="text-xs muted mono">{d.updated_at.slice(0, 16).replace('T', ' ')}</td>
                    <td>
                      <Show when={d.is_owner}>
                        <button
                          class="btn-icon"
                          title="Удалить"
                          onClick={(ev) => {
                            ev.stopPropagation()
                            onDelete(d)
                          }}
                        >
                          <Icon name="x" />
                        </button>
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
  )
}

// ────────────────────── редактор ──────────────────────

type EditorProps = {
  /// `null` — режим «новая компоновка». Иначе — редактируем существующую.
  drillstringId: number | null
  onBack:        () => void
  onCreated:     (id: number) => void
  onOpenTool:    (id: string) => void
}

type SectionDraft = {
  /// Локальный uid для key= в <For>. Бэк назначит реальный id при сохранении,
  /// но For нужен стабильный ключ при добавлении/удалении.
  uid:      number
  tool_id:  string | null  // Uuid
  quantity: number
}

let nextUid = 1
const newDraftUid = () => nextUid++

const DrillstringEditor: Component<EditorProps> = (props) => {
  const [tools] = createResource(listTools)

  const [detail, { refetch: refetchDetail }] = createResource(
    () => props.drillstringId,
    async (id) => (id == null ? null : await getDrillstring(id)),
  )

  const [name, setName] = createSignal('')
  const [description, setDescription] = createSignal('')
  const [sections, setSections] = createSignal<SectionDraft[]>([])
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  // DnD state. dragIdx — что тащим, dropIdx — над какой строкой курсор.
  const [dragIdx, setDragIdx] = createSignal<number | null>(null)
  const [dropIdx, setDropIdx] = createSignal<number | null>(null)

  // Заливаем detail в локальный стейт. createMemo пересчитывается при смене
  // detail() (например после сохранения refetchDetail).
  createMemo(() => {
    const d = detail()
    if (d) {
      setName(d.name)
      setDescription(d.description)
      setSections(
        d.sections.map((s) => ({
          uid:      newDraftUid(),
          tool_id:  s.tool_id,
          quantity: s.quantity,
        })),
      )
    } else if (props.drillstringId == null && !detail.loading) {
      setName('')
      setDescription('')
      setSections([])
    }
  })

  const isCreate = () => props.drillstringId == null
  // Read-only — компоновка расшарена со мной кем-то ещё. Нельзя править,
  // удалить, шарить дальше.
  const isReadOnly = createMemo<boolean>(() => detail()?.is_owner === false)
  // toolById объединяет мой собственный каталог + `tools_resolved` из
  // detail (нужен для shared-recipient: его catalog не содержит чужого
  // инструмента, секции бы рендерились как «отсутствует в каталоге»).
  const toolById = createMemo<Record<string, ToolDto>>(() => {
    const map: Record<string, ToolDto> = {}
    for (const t of tools() ?? []) map[t.id] = t
    for (const t of detail()?.tools_resolved ?? []) map[t.id] = t
    return map
  })

  // Группировка по kind для <optgroup> в селекторе. Сохраняем порядок
  // TOOL_KINDS, чтобы UI был стабилен между сессиями.
  const toolsByKind = createMemo<Partial<Record<ToolKind, ToolDto[]>>>(() => {
    const items = tools() ?? []
    const groups: Partial<Record<ToolKind, ToolDto[]>> = {}
    for (const t of items) {
      if (!groups[t.kind]) groups[t.kind] = []
      groups[t.kind]!.push(t)
    }
    return groups
  })

  // ────── dirty state ──────
  // Ключ для активации «Сохранить» и confirm-on-back. При создании — любая
  // непустая строка/секция считается изменением. При редактировании —
  // diff с detail().

  const isDirty = createMemo<boolean>(() => {
    if (isCreate()) {
      return (
        name().trim().length > 0 ||
        description().trim().length > 0 ||
        sections().length > 0
      )
    }
    const d = detail()
    if (!d) return false
    if (d.name !== name()) return true
    if (d.description !== description()) return true
    const cur = sections()
    if (d.sections.length !== cur.length) return true
    return d.sections.some((s, i) => {
      const c = cur[i]!
      return s.tool_id !== c.tool_id || s.quantity !== c.quantity
    })
  })

  const tryBack = () => {
    if (isDirty() && !confirm('Несохранённые изменения будут потеряны. Продолжить?')) return
    props.onBack()
  }

  // beforeunload — последний рубеж для tab-close/refresh. Показывает нативный
  // браузерный диалог (текст не настраиваем — современные браузеры игнорируют).
  onMount(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    onCleanup(() => window.removeEventListener('beforeunload', handler))
  })

  // ────── изменения секций ──────

  const addSection = () => {
    setSections([
      ...sections(),
      { uid: newDraftUid(), tool_id: null, quantity: 1 },
    ])
  }

  const removeSection = (uid: number) => {
    setSections(sections().filter((s) => s.uid !== uid))
  }

  const updateSection = (uid: number, patch: Partial<SectionDraft>) => {
    setSections(sections().map((s) => (s.uid === uid ? { ...s, ...patch } : s)))
  }

  const moveSection = (uid: number, dir: -1 | 1) => {
    const arr = sections().slice()
    const idx = arr.findIndex((s) => s.uid === uid)
    const newIdx = idx + dir
    if (idx < 0 || newIdx < 0 || newIdx >= arr.length) return
    const [item] = arr.splice(idx, 1)
    arr.splice(newIdx, 0, item!)
    setSections(arr)
  }

  const moveSectionByIdx = (from: number, to: number) => {
    if (from === to) return
    const arr = sections().slice()
    if (from < 0 || from >= arr.length || to < 0 || to >= arr.length) return
    const [item] = arr.splice(from, 1)
    arr.splice(to, 0, item!)
    setSections(arr)
  }

  // ────── сохранение ──────

  const collectSectionInputs = (): DrillstringSectionInput[] | string => {
    const secs = sections()
    const out: DrillstringSectionInput[] = []
    for (let i = 0; i < secs.length; i++) {
      const s = secs[i]!
      // Номер в сообщении — как в UI (нумерация снизу: #1 = последняя строка).
      const displayNo = secs.length - i
      if (s.tool_id == null) return `Секция ${displayNo}: выберите инструмент`
      if (!Number.isInteger(s.quantity) || s.quantity < 1) {
        return `Секция ${displayNo}: количество должно быть ≥ 1`
      }
      out.push({ tool_id: s.tool_id, quantity: s.quantity })
    }
    return out
  }

  const onSave = async () => {
    const trimmedName = name().trim()
    if (trimmedName.length === 0) {
      setErr('Название обязательно')
      return
    }
    const inputs = collectSectionInputs()
    if (typeof inputs === 'string') {
      setErr(inputs)
      return
    }

    setBusy(true)
    setErr(null)
    try {
      if (isCreate()) {
        const created = await createDrillstring({
          name:        trimmedName,
          description: description(),
          sections:    inputs,
        })
        props.onCreated(created.id)
        return
      }

      const id = props.drillstringId!
      const d = detail()!
      const headerChanged = d.name !== trimmedName || d.description !== description()
      const sectionsChanged =
        d.sections.length !== inputs.length ||
        d.sections.some((s, i) => {
          const inp = inputs[i]!
          return s.tool_id !== inp.tool_id || s.quantity !== inp.quantity
        })

      if (headerChanged) {
        await updateDrillstringHeader(id, {
          name:        trimmedName,
          description: description(),
        })
      }
      if (sectionsChanged) {
        await replaceSections(id, { sections: inputs })
      }
      refetchDetail()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  // Сводка по секции — kind/title/основные параметры — для строки в списке.
  // Берём первые 3 поля meta с display-значениями (mm для диаметров, m для длин).
  const sectionMeta = (s: SectionDraft): string => {
    if (s.tool_id == null) return '—'
    const t = toolById()[s.tool_id]
    if (!t) return `${s.tool_id.slice(0, 8)} · отсутствует в каталоге`
    const k = KIND_BY_ID[t.kind]
    const body = unwrapPayload(t.kind, t.payload)
    const dims =
      k && body && k.fields.length > 0
        ? k.fields
            .slice(0, 3)
            .map((f) => {
              const raw = body[f.key]
              const num = typeof raw === 'number' ? raw : 0
              return `${f.label}=${fromSi(num, f.unit).toFixed(f.unit === 'mm' ? 1 : 3)}`
            })
            .join(' · ')
        : ''
    return dims ? `${kindLabel(t.kind)} · ${dims}` : kindLabel(t.kind)
  }

  const openTool = (e: MouseEvent, toolId: string) => {
    e.preventDefault()
    e.stopPropagation()
    props.onOpenTool(toolId)
  }

  // ────── share modal ──────

  const [shareOpen, setShareOpen] = createSignal(false)

  // ────── render ──────

  return (
    <div class="page">
      <div class="page-header">
        <div class="row" style={{ gap: 'var(--sp-3)' }}>
          <button class="btn btn-ghost" onClick={tryBack}>
            ← К списку
          </button>
          <div>
            <h1 class="page-title">
              {isCreate() ? 'Новая компоновка' : `Компоновка #${props.drillstringId}`}
            </h1>
            <p class="page-subtitle">
              Порядок сверху вниз — устье → забой
              <Show when={isDirty()}>
                <span style={{ 'margin-left': 'var(--sp-3)', color: 'var(--status-warn)' }}>
                  · несохранённые изменения
                </span>
              </Show>
            </p>
          </div>
        </div>
        <div class="row">
          <Show when={!isCreate() && !isReadOnly()}>
            <button class="btn btn-ghost" onClick={() => setShareOpen(true)}>
              <Icon name="user" />Поделиться
            </button>
          </Show>
          <Show when={!isReadOnly()}>
            <button class="btn btn-primary" onClick={onSave} disabled={busy() || !isDirty()}>
              {busy() ? 'Сохраняем…' : isCreate() ? 'Создать' : 'Сохранить'}
            </button>
          </Show>
        </div>
      </div>

      <Show when={isReadOnly()}>
        {(() => {
          const d = detail()!
          return (
            <div
              class="card"
              style={{
                padding: 'var(--sp-4)',
                'margin-bottom': 'var(--sp-4)',
                'border-left': '3px solid var(--status-info, var(--accent))',
              }}
            >
              <div style={{ 'font-weight': 500 }}>Расшарено вам</div>
              <div class="muted text-sm" style={{ 'margin-top': 'var(--sp-1)' }}>
                Владелец — {d.owner_name || d.owner_email || '?'}
                <Show when={d.owner_name && d.owner_email}>
                  <span class="muted"> ({d.owner_email})</span>
                </Show>
                . Просмотр без возможности редактирования.
              </div>
            </div>
          )
        })()}
      </Show>

      <Show when={detail.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={detail.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка загрузки: {String(detail.error)}
        </div>
      </Show>

      <Show when={!detail.loading && !detail.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', 'margin-bottom': 'var(--sp-5)' }}>
          <div style={{ display: 'flex', 'flex-direction': 'column', gap: 'var(--sp-4)' }}>
            <label class="field">
              <div class="field-label">Название</div>
              <input
                class="field-input"
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                placeholder="Напр., КНБК Ø 215.9 · 2-й интервал"
                readOnly={isReadOnly()}
              />
            </label>
            <label class="field">
              <div class="field-label">Описание</div>
              <textarea
                class="field-input"
                rows={2}
                value={description()}
                onInput={(e) => setDescription(e.currentTarget.value)}
                placeholder="Произвольное примечание"
                readOnly={isReadOnly()}
              />
            </label>
          </div>
        </div>

        <div class="section-head">
          <h2>Секции ({sections().length})</h2>
          <Show when={!isReadOnly()}>
            <button class="btn" onClick={addSection}>
              <Icon name="plus" />Добавить секцию
            </button>
          </Show>
        </div>

        <Show when={tools.error}>
          <div class="card" style={{ padding: 'var(--sp-4)', color: 'var(--status-err)' }}>
            Не удалось загрузить каталог: {String(tools.error)}
          </div>
        </Show>

        <Show when={tools() && tools()!.length === 0}>
          <div class="card" style={{ padding: 'var(--sp-5)', 'text-align': 'center' }}>
            <div class="muted">Каталог пуст — добавьте инструмент в разделе «Каталог», чтобы собирать компоновки</div>
          </div>
        </Show>

        <Show when={sections().length === 0 && tools() && tools()!.length > 0}>
          <div class="card" style={{ padding: 'var(--sp-5)', 'text-align': 'center' }}>
            <div class="muted">Пусто — добавьте первую секцию</div>
          </div>
        </Show>

        <div class="drillstring-editor-grid">
        <div class="section-list">
          <For each={sections()}>
            {(s, i) => {
              const isDragging = () => dragIdx() === i()
              const isDropTarget = () => dropIdx() === i() && dragIdx() !== null && dragIdx() !== i()
              const unselected = () => s.tool_id == null
              return (
                <div
                  class={
                    'card section-row' +
                    (isDragging() ? ' dragging' : '') +
                    (isDropTarget() ? ' drop-target' : '') +
                    (unselected() ? ' invalid' : '')
                  }
                  draggable={!isReadOnly()}
                  onDragStart={(e) => {
                    setDragIdx(i())
                    e.dataTransfer!.effectAllowed = 'move'
                    // setData нужен для firefox — без него dragstart игнорируется.
                    e.dataTransfer!.setData('text/plain', String(i()))
                  }}
                  onDragOver={(e) => {
                    if (dragIdx() === null) return
                    e.preventDefault()
                    e.dataTransfer!.dropEffect = 'move'
                    setDropIdx(i())
                  }}
                  onDragLeave={() => {
                    if (dropIdx() === i()) setDropIdx(null)
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    const from = dragIdx()
                    setDragIdx(null)
                    setDropIdx(null)
                    if (from === null) return
                    moveSectionByIdx(from, i())
                  }}
                  onDragEnd={() => {
                    setDragIdx(null)
                    setDropIdx(null)
                  }}
                >
                  <div class="section-handle" title="Перетащите для перестановки">⋮⋮</div>
                  {/* Нумерация снизу: #1 — последняя строка списка, #N — первая.
                      Порядок строк остаётся в data-конвенции (i=0 = position=0 = долото
                      сверху), но колонка «#» считается с конца. */}
                  <div class="section-pos mono">{sections().length - i()}</div>
                  <div class="section-main">
                    <select
                      class="field-input"
                      value={s.tool_id ?? ''}
                      disabled={isReadOnly()}
                      onChange={(e) => {
                        const v = e.currentTarget.value
                        updateSection(s.uid, { tool_id: v === '' ? null : v })
                      }}
                    >
                      <option value="">— выберите инструмент —</option>
                      <For each={TOOL_KINDS}>
                        {(k) => {
                          const list = () => toolsByKind()[k.id] ?? []
                          return (
                            <Show when={list().length > 0}>
                              <optgroup label={k.label}>
                                <For each={list()}>
                                  {(t) => (
                                    <option value={t.id} selected={s.tool_id === t.id}>
                                      {t.title}
                                    </option>
                                  )}
                                </For>
                              </optgroup>
                            </Show>
                          )
                        }}
                      </For>
                    </select>
                    <div class="section-meta">
                      {sectionMeta(s)}
                      <Show when={s.tool_id != null && toolById()[s.tool_id!]}>
                        {(t) => (
                          <a
                            class="section-tool-link"
                            href={router.buildLocation({ to: '/tools', search: { edit: t().id } }).href}
                            onClick={(e) => openTool(e, t().id)}
                          >
                            Открыть инструмент
                          </a>
                        )}
                      </Show>
                    </div>
                  </div>
                  <div class="section-qty">
                    <span class="field-label" style={{ 'min-width': '12px' }}>×</span>
                    <input
                      class="field-input mono"
                      style={{ width: '64px' }}
                      type="number"
                      min="1"
                      value={s.quantity}
                      readOnly={isReadOnly()}
                      onInput={(e) => {
                        const n = parseInt(e.currentTarget.value, 10)
                        updateSection(s.uid, { quantity: Number.isFinite(n) && n >= 1 ? n : 1 })
                      }}
                    />
                  </div>
                  <Show when={!isReadOnly()}>
                    <div class="section-tools">
                      <button
                        class="btn-icon btn-ghost"
                        title="Вверх"
                        onClick={() => moveSection(s.uid, -1)}
                        disabled={i() === 0}
                      >
                        ↑
                      </button>
                      <button
                        class="btn-icon btn-ghost"
                        title="Вниз"
                        onClick={() => moveSection(s.uid, 1)}
                        disabled={i() === sections().length - 1}
                      >
                        ↓
                      </button>
                      <button
                        class="btn-icon btn-ghost"
                        title="Удалить"
                        onClick={() => removeSection(s.uid)}
                      >
                        <Icon name="x" />
                      </button>
                    </div>
                  </Show>
                </div>
              )
            }}
          </For>
        </div>

        <aside class="drillstring-schematic-col">
          <div class="drillstring-schematic-sticky">
            <div class="drillstring-schematic-title">Схема</div>
            <DrillstringSchematic
              sections={sections()}
              toolById={toolById()}
            />
          </div>
        </aside>
        </div>

        <Show when={err()}>
          <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-top': 'var(--sp-4)', color: 'var(--status-err)' }}>
            {err()}
          </div>
        </Show>
      </Show>

      <Show when={shareOpen() && props.drillstringId != null}>
        <ShareModal
          drillstringId={props.drillstringId!}
          onClose={() => setShareOpen(false)}
        />
      </Show>
    </div>
  )
}

// ────────────────────── share modal ──────────────────────

type ShareModalProps = {
  drillstringId: number
  onClose: () => void
}

const ShareModal: Component<ShareModalProps> = (props) => {
  const [shares, { refetch }] = createResource(
    () => props.drillstringId,
    (id) => listShares(id),
  )
  const [email, setEmail] = createSignal('')
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const onAdd = async () => {
    const value = email().trim()
    if (!value) return
    setBusy(true)
    setErr(null)
    try {
      await createShare(props.drillstringId, value)
      setEmail('')
      refetch()
    } catch (e) {
      const msg = String(e)
      // Сервер отдаёт 404, если public_user с таким email не найден.
      // Подменяем на человекочитаемый текст.
      if (msg.includes('404')) {
        setErr('Пользователь с таким email не найден на lostcirculation.life')
      } else if (msg.includes('400')) {
        setErr('Нельзя расшарить эту компоновку (пустой email или сами себе)')
      } else {
        setErr(msg)
      }
    } finally {
      setBusy(false)
    }
  }

  const onRevoke = async (s: ShareDto) => {
    if (!confirm(`Отозвать доступ у ${s.email ?? `user#${s.granted_to_user_id}`}?`)) return
    setErr(null)
    try {
      await deleteShare(props.drillstringId, s.granted_to_user_id)
      refetch()
    } catch (e) {
      setErr(String(e))
    }
  }

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') props.onClose()
  }

  return (
    <div
      class="modal-overlay visible"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose()
      }}
      onKeyDown={onKey}
    >
      <div
        class="search-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ 'max-width': '560px' }}
      >
        <div
          class="row"
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            'border-bottom': '1px solid var(--line-soft)',
          }}
        >
          <h3 style={{ flex: 1, margin: 0 }}>Поделиться компоновкой</h3>
          <button class="btn-icon" onClick={props.onClose} title="Закрыть">
            <Icon name="x" />
          </button>
        </div>
        <div style={{ padding: 'var(--sp-5)' }}>
          <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-3)' }}>
            Получатели увидят компоновку в своём списке без возможности
            редактирования. Доступ всегда можно отозвать.
          </div>
          <div class="row" style={{ gap: 'var(--sp-2)', 'margin-bottom': 'var(--sp-4)' }}>
            <input
              class="field-input"
              style={{ flex: 1 }}
              type="email"
              placeholder="email@example.com"
              value={email()}
              onInput={(e) => setEmail(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onAdd()
              }}
            />
            <button class="btn btn-primary" onClick={onAdd} disabled={busy() || !email().trim()}>
              {busy() ? '…' : 'Добавить'}
            </button>
          </div>

          <Show when={err()}>
            <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
              {err()}
            </div>
          </Show>

          <div class="text-sm" style={{ 'margin-bottom': 'var(--sp-2)', 'font-weight': 500 }}>
            Кому уже расшарено
          </div>
          <Show when={shares.loading}>
            <div class="muted">Загрузка…</div>
          </Show>
          <Show when={!shares.loading && (shares() ?? []).length === 0}>
            <div class="muted">Никому</div>
          </Show>
          <Show when={(shares() ?? []).length > 0}>
            <div class="card" style={{ padding: 0 }}>
              <For each={shares()!}>
                {(s) => (
                  <div
                    class="row"
                    style={{
                      padding: 'var(--sp-2) var(--sp-3)',
                      'border-bottom': '1px solid var(--line-soft)',
                    }}
                  >
                    <div style={{ flex: 1, 'min-width': 0 }}>
                      <div style={{ 'font-weight': 500, overflow: 'hidden', 'text-overflow': 'ellipsis', 'white-space': 'nowrap' }}>
                        {s.name || s.email || `user#${s.granted_to_user_id}`}
                      </div>
                      <div class="text-xs muted" style={{ overflow: 'hidden', 'text-overflow': 'ellipsis', 'white-space': 'nowrap' }}>
                        {s.email ?? '—'}
                      </div>
                    </div>
                    <button class="btn-icon btn-ghost" title="Отозвать" onClick={() => onRevoke(s)}>
                      <Icon name="x" />
                    </button>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </div>
      </div>
    </div>
  )
}

// ────────────────────── корневой компонент ──────────────────────

type DrillstringPageProps = {
  drillstringId?: number | null
  create?: boolean
  onOpen: (id: number) => void
  onCreate: () => void
  onBack: () => void
  onOpenTool: (id: string) => void
}

export const DrillstringPage: Component<DrillstringPageProps> = (props) => {
  const editing = () => props.create === true || props.drillstringId != null

  return (
    <Show
      when={editing()}
      fallback={
        <DrillstringList
          onOpen={props.onOpen}
          onCreate={props.onCreate}
        />
      }
    >
      <DrillstringEditor
        drillstringId={props.create ? null : props.drillstringId ?? null}
        onBack={props.onBack}
        onCreated={props.onOpen}
        onOpenTool={props.onOpenTool}
      />
    </Show>
  )
}
